import { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { cn } from '@/lib/utils';

const METRICS = [
	{ key: 'impressions', label: 'Impressions' },
	{ key: 'clicks', label: 'Clicks' },
	{ key: 'ctr', label: 'CTR' },
	{ key: 'cpc', label: 'CPC' },
	{ key: 'spend', label: 'Spend' }
];

const safeNumber = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0);

// Each metric points at a `{value, prev}` field pair on the per-day series
// the handler returns. CTR is derived per-day from impressions/clicks so the
// shape matches whether or not the handler emits a precomputed `ctr` column.
function transform(series, metric) {
	return series.map((d) => {
		const impressions = safeNumber(d.impressions);
		const clicks = safeNumber(d.clicks);
		const prevImpressions = safeNumber(d.prevImpressions);
		const prevClicks = safeNumber(d.prevClicks);
		let value;
		let prev;
		if (metric === 'impressions') {
			value = impressions;
			prev = prevImpressions;
		} else if (metric === 'clicks') {
			value = clicks;
			prev = prevClicks;
		} else if (metric === 'ctr') {
			value = impressions ? Number(((clicks / impressions) * 100).toFixed(2)) : 0;
			prev = prevImpressions ? Number(((prevClicks / prevImpressions) * 100).toFixed(2)) : 0;
		} else if (metric === 'cpc') {
			value = safeNumber(d.cpc);
			prev = safeNumber(d.prevCpc);
		} else {
			// Real per-day spend (the budget that was active that day), not a
			// flat constant. Honest to history even when the user changed their
			// daily budget mid-window.
			value = safeNumber(d.spend);
			prev = safeNumber(d.prevSpend);
		}
		return { date: d.date, value, prev };
	});
}

export function AdsPerformanceChart({ series = [] }) {
	const [metric, setMetric] = useState('impressions');
	const data = useMemo(() => transform(series, metric), [series, metric]);

	return (
		<div>
			<div className="mb-5 flex flex-wrap gap-2">
				{METRICS.map((m) => (
					<button
						key={m.key}
						type="button"
						onClick={() => setMetric(m.key)}
						className={cn(
							'rounded-full px-3.5 py-1.5 text-xs font-semibold transition',
							metric === m.key ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground hover:bg-muted/70'
						)}
					>
						{m.label}
					</button>
				))}
			</div>
			<div className="h-64 w-full">
				<ResponsiveContainer width="100%" height="100%">
					<LineChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
						<CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
						<XAxis
							dataKey="date"
							tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
							tickLine={false}
							axisLine={false}
							interval={4}
						/>
						<YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} width={40} />
						<Tooltip
							contentStyle={{
								background: 'hsl(var(--popover))',
								border: '1px solid hsl(var(--border))',
								borderRadius: 8,
								fontSize: 12
							}}
						/>
						<Line
							type="monotone"
							dataKey="prev"
							stroke="hsl(var(--muted-foreground))"
							strokeWidth={1.5}
							strokeDasharray="4 4"
							dot={false}
							name="Previous period"
						/>
						<Line
							type="monotone"
							dataKey="value"
							stroke="hsl(var(--primary))"
							strokeWidth={2.5}
							dot={false}
							name="This period"
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
}
